/** Responsive layout category; it describes the current viewport, not the hardware. */
export type DeviceKind = 'Phone' | 'Tablet' | 'Desktop'
export type DevicePosture = 'continuous' | 'folded' | null

export interface DisplayRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface DisplayCrease extends DisplayRect {
  readonly orientation: 'vertical' | 'horizontal'
}

export interface DisplayInfo {
  readonly width: number
  readonly height: number
  /** Null means the browser does not report posture. */
  readonly posture: DevicePosture
  readonly segments: readonly DisplayRect[]
  /** A zero-width/height crease is a reported fold boundary without an occluding gap. */
  readonly creases: readonly DisplayCrease[]
  readonly segmented: boolean
}

interface ChangeSource {
  addEventListener?(type: string, listener: () => void): void
  removeEventListener?(type: string, listener: () => void): void
}

export interface DeviceWindow extends ChangeSource {
  innerWidth?: number
  innerHeight?: number
  document?: { documentElement?: { clientWidth: number, clientHeight: number } }
  navigator?: { devicePosture?: ChangeSource & { type?: string } }
  screen?: { orientation?: ChangeSource }
  viewport?: { segments?: readonly DisplayRect[] | null }
  visualViewport?: ChangeSource & { segments?: readonly DisplayRect[] | null }
  getWindowSegments?(): readonly DisplayRect[]
}

export const MIN_MEDIUM_SIZE = 600
export const MIN_LARGE_SIZE = 1280

export function resolveDevice(userAgent: string, display: DisplayInfo, override?: DeviceKind): DeviceKind {
  if (override) return override

  if (display.width > 0) {
    if (display.width < MIN_MEDIUM_SIZE) return 'Phone'
    if (display.width < MIN_LARGE_SIZE) return 'Tablet'
    return 'Desktop'
  }

  // Server-side clients and browsers without viewport information retain UA detection.
  if (/iPad/i.test(userAgent)) return 'Tablet'
  if (/Mobile|iPhone|iPod|IEMobile|Opera Mini/i.test(userAgent)) return 'Phone'
  if (/Android|Kindle|Silk/i.test(userAgent)) return 'Tablet'
  return 'Desktop'
}

function normalizeRect(rect: DisplayRect): DisplayRect | null {
  if (![rect.x, rect.y, rect.width, rect.height].every(Number.isFinite)) return null
  if (rect.width <= 0 || rect.height <= 0) return null
  return Object.freeze({ x: rect.x, y: rect.y, width: rect.width, height: rect.height })
}

function readSegments(window?: DeviceWindow): readonly DisplayRect[] {
  if (!window) return Object.freeze([])
  let segments: readonly DisplayRect[] | null | undefined
  try {
    // Current CSS Viewport API, followed by older Chromium implementations.
    segments = window.viewport?.segments ?? window.visualViewport?.segments ?? window.getWindowSegments?.()
  } catch {
    // Experimental APIs may be unavailable in an inactive document.
    return Object.freeze([])
  }
  if (!Array.isArray(segments)) return Object.freeze([])
  return Object.freeze(segments.map(normalizeRect).filter((rect): rect is DisplayRect => !!rect))
}

function detectCreases(segments: readonly DisplayRect[]): readonly DisplayCrease[] {
  const creases = new Map<string, DisplayCrease>()
  for (let first = 0; first < segments.length; first++) {
    for (let second = first + 1; second < segments.length; second++) {
      const a = segments[first]
      const b = segments[second]
      let crease: DisplayCrease | null = null
      if (a.y === b.y && a.height === b.height) {
        const left = a.x < b.x ? a : b
        const right = left === a ? b : a
        const gap = right.x - left.x - left.width
        if (gap >= 0) crease = { orientation: 'vertical', x: left.x + left.width, y: left.y, width: gap, height: left.height }
      } else if (a.x === b.x && a.width === b.width) {
        const top = a.y < b.y ? a : b
        const bottom = top === a ? b : a
        const gap = bottom.y - top.y - top.height
        if (gap >= 0) crease = { orientation: 'horizontal', x: top.x, y: top.y + top.height, width: top.width, height: gap }
      }
      if (crease) {
        // Only adjacent segments define a crease; never treat another pane as a hinge.
        const spansOtherSegment = segments.some(segment => segment !== a && segment !== b && (
          crease.orientation === 'vertical'
            ? segment.x < crease.x + crease.width && segment.x + segment.width > crease.x
            : segment.y < crease.y + crease.height && segment.y + segment.height > crease.y
        ))
        if (!spansOtherSegment) creases.set(JSON.stringify(crease), Object.freeze(crease))
      }
    }
  }
  return Object.freeze([...creases.values()])
}

export function readDisplay(window?: DeviceWindow): DisplayInfo {
  // Use the layout viewport. Pinch zoom and the on-screen keyboard must not change the layout category.
  const width = window?.document?.documentElement?.clientWidth || window?.innerWidth || 0
  const height = window?.document?.documentElement?.clientHeight || window?.innerHeight || 0
  const type = window?.navigator?.devicePosture?.type
  const posture = type === 'folded' || type === 'continuous' ? type : null
  const segments = readSegments(window)
  return Object.freeze({ width, height, posture, segments, creases: detectCreases(segments), segmented: segments.length > 1 })
}

/** Subscribe to standard resize, orientation and posture changes; clean up as one unit. */
export function watchDisplay(window: DeviceWindow, update: () => void): () => void {
  const sources: Array<[ChangeSource | undefined, string]> = [
    [window, 'resize'],
    [window.screen?.orientation, 'change'],
    [window.navigator?.devicePosture, 'change'],
    [window.visualViewport, 'resize'],
  ]
  for (const [source, event] of sources) source?.addEventListener?.(event, update)
  return () => {
    for (const [source, event] of sources) source?.removeEventListener?.(event, update)
  }
}
